import axios from "axios";
import React, { useState} from "react";
import { SpinnerComponent } from "../../commonComponent/SpinnerComponent";
import { APIKey, FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { CheckPlateRequest, SERVICE_CALL_RESPONSE_CODES } from "../../entities/PlpType";
import { PASSENGER_PLATE, NO_GRAPHIC, MOTORCYCLE_PLATE, GRAPHIC, constructPlateCanvasAltText, ENGLISH_SLOGAN } from "../../utilities/custom/PlpUtils";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import SessionStorageService from "../../services/SessionStorageService";
import { setBaseHeaders } from "../../utilities/submitUtils";
import Utils from "../../utilities/Utils";
import ReCaptchaV3 from "../captcha/CaptchComponent";
import ReCaptchaButton from "../captcha/captcha-button";
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";
import { InformationalAlertComponent } from "../odsCalloutBox/InformationalAlertComponent";
import PlateImageComponent from "./PlateImageComponent";
import { getParentSection,findSectionElementByFieldName } from "../../utilities/ElementUtils";

interface Props {
  field: FormSectionElements;
  labelControlArray: any;
  requiredField: boolean;
  isEng: boolean;
  callbackStepIndex: (index: number, returnIndex?: number) => void;
  callbackElement: (programContainer: ProgramContainer) => void;
  callbackValidate?: (errors: any, fromAPI?: boolean) => void
}

/**
 * A component that handles the plate-characters validation check and invoke the PlateImage to display the preview of the plate.
 */
const CheckPlateAndRenderImageComponent = ({field, labelControlArray, requiredField, isEng, callbackValidate,callbackStepIndex, callbackElement}: Props) => {
  const PLATE_ALREADY_EXISTS_IN_ORDER = '60';
  const [plateType, setPlateType] = useState(field.parent.ListOfFormSectionElements[0]?.Value || PASSENGER_PLATE);  // plateType
  const [sloganChoice, setSloganChoice] = useState(field.parent.ListOfFormSectionElements[1]?.Value || NO_GRAPHIC);  // sloganType
  const [graphicChoice, setGraphicChoice] = useState(field.parent.ListOfFormSectionElements[5]?.Value);
  const [wantGraphic, setWantGraphic] = useState(field.parent.ListOfFormSectionElements[2]?.Value || NO_GRAPHIC);
  const [redrawPlate, setRedrawPlate] = useState(false);

  const handleOnChange = (e) => {
    let val = e.target.value;

    field.Value = val ? val.toUpperCase() : val;  // Convert the input to uppercase
  
    const plateType = field.parent.ListOfFormSectionElements[0]?.Value || PASSENGER_PLATE;  // plateType
    const wantGraphic = field.parent.ListOfFormSectionElements[2]?.Value || NO_GRAPHIC // wantGraphic
    const programContainer = field.parent.parent.parent as ProgramContainer;
    FormValidationUtils.validateLicensePlate(field, plateType, wantGraphic, programContainer.CategoryId);
    if (!field.Valid) {
      field.ErrorMessage = 'This message does not meet all of the requirements above. Please enter a different plate message.';  // Update the error message content.
      field.ErrorMessageFRA = 'Ce message ne répond pas à toutes les exigences ci-dessus. Veuillez entrer un autre message de plaque.';
    }

    callbackElement(field.parent.parent.parent);
  }
  
  const handleOnBlur = (e) => {
    const plateType = field.parent.ListOfFormSectionElements[0]?.Value || PASSENGER_PLATE;  // plateType
    const wantGraphic = field.parent.ListOfFormSectionElements[2]?.Value || NO_GRAPHIC; // wantGraphic
    const programContainer = field.parent.parent.parent as ProgramContainer;
    FormValidationUtils.evalLicensePlateRegularExpression(field, plateType, wantGraphic, programContainer.CategoryId);
  
    if (field.Valid) {
      FormValidationUtils.runJavascript(field);
    } else {
      setShowPlateImage(false); // Hide the plate-image
    }
     // FormValidationUtils can't handle asyn-validation, so check-plate needs to be performed in onBlur.
    if (field.Value) {
      doCheckPlate();
    }
    callbackElement(field.parent.parent.parent);
  }

  const doCheckPlate = () => {
    const buttonName = `button_${field.parent.Sequence}_${field.Id}`;
    const button = document.getElementById(buttonName);
    if (button) {
      button.click();
    }
  }

  const handleButtonClick = async (captchaToken?: string) => {
    // type of plate
    const plateType = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'plateType')?.Value;
    // slogan language
    const sloganLanguage = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'sloganLanguage')?.Value;
    // graphic selection
    const wantGraphic = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'wantGraphic')?.Value || NO_GRAPHIC;
    //plate Quantity
    const plateQuantity = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'plateCount')?.Value;


    let graphicType = NO_GRAPHIC;
    if (wantGraphic === GRAPHIC) {
        graphicType = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'graphicPlateSearch')?.Value || findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'graphicType')?.Value;
    }

    if (field.enableCheckPlate) {
      if (isDependentValuesExisted(plateType, plateType === MOTORCYCLE_PLATE ? ENGLISH_SLOGAN : sloganLanguage, wantGraphic, graphicType, plateQuantity, true)) {
        // Need to re-validate the input plate characters again.
        field.Valid = true;  // Reset validity of the field to force a revalidation.
        const programContainer = field.parent.parent.parent as ProgramContainer;
        FormValidationUtils.evalLicensePlateRegularExpression(field, plateType, wantGraphic, programContainer.CategoryId);
        if (!field.Valid) {
          field.ErrorMessage = 'This message does not meet all of the requirements above. Please enter a different plate message.';  // Update the error message content.
          field.ErrorMessageFRA = 'Ce message ne répond pas à toutes les exigences ci-dessus. Veuillez entrer un autre message de plaque.';
          callbackElement(field.parent.parent.parent);
        }
      } else {
        field.Valid = false;
        field.Touched = true;
        field.IsEmpty = false;
        field.ErrorMessage = 'Perform \'Check Plate\' when the required selection criteria is/are entered.';
        field.ErrorMessageFRA = 'Exécutez « Vérifier la plaque » lorsque les critères de sélection requis sont saisis.';
        callbackElement(field.parent.parent.parent);
        return;
      }

      if (!field.Valid) {
        return;
      }
      if ((field.Value?.trim().length > 0) && (field.parent?.ListOfFormSectionElements?.length > 0)) {
        checkPlate(plateType, plateType === MOTORCYCLE_PLATE ? ENGLISH_SLOGAN : sloganLanguage, graphicType, captchaToken).then((success) => {
          if (success) {
            setPlateType(plateType);
            if (plateType === MOTORCYCLE_PLATE) {
              findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'sloganLanguage').Value = ENGLISH_SLOGAN;
            }
            setSloganChoice(sloganLanguage || ENGLISH_SLOGAN);
            setGraphicChoice(graphicType);
            setRedrawPlate(true)
            setShowPlateImage(true);
            resetInputError();
            updateHiddenFields(plateType, plateType === MOTORCYCLE_PLATE ? ENGLISH_SLOGAN : sloganLanguage, graphicType);
          } else {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
            setShowPlateImage(false); // Hide the plate-image
          }
        });
      } else {
        handleEmptyInputError();
        setShowPlateImage(false); // Hide the plate-image
      }
    } else {
      if (isDependentValuesExisted(plateType, sloganLanguage, wantGraphic, graphicType, plateQuantity,false)) {
        field.Value = field.defaultPlateCharacters || 'AAA111';
        setPlateType(plateType);
        setSloganChoice(sloganLanguage);
        setGraphicChoice(graphicType);
        setRedrawPlate(true)
        setShowPlateImage(true);
        resetInputError();
        updateHiddenFields(plateType, sloganLanguage, graphicType);
      } else {
        let errorneousField: FormSectionElements;
        if (!Utils.isNotNullOrEmpty(plateType)) {
          errorneousField = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'plateType');
        } else if (!Utils.isNotNullOrEmpty(sloganLanguage)) {
          errorneousField = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'sloganLanguage');
        } else if (!Utils.isNotNullOrEmpty(graphicType))  {
          errorneousField = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'graphicCategory');
        }
        else {
          errorneousField = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, 'plateCount');
        }
        errorneousField.Valid = false;
        errorneousField.Touched = true;
        errorneousField.IsEmpty = true;
        callbackElement(field.parent.parent.parent);
        const section = getParentSection(errorneousField);  // Get the section that contains the field with an error
        const invalidControlList = FormValidationUtils.invalidControls(section); // Find all invalid fields in that section
        // If there are invalid fields, show errors to the user
        if (invalidControlList?.length > 0) {
          callbackValidate?.(invalidControlList);
              window.scrollTo(0, 0);  // Scroll to top so user sees errors
              const errorContainer = document.querySelector('#error-container') as HTMLDivElement;
              errorContainer?.focus();
        }
       return;
      }
    }
  }

  const isDependentValuesExisted = (plateType: string, sloganLanguage: string, wantGraphic: string, graphicType: string, plateQuantity: string, enableCheckPlate: boolean) => {
    return (plateType && sloganLanguage && wantGraphic  && graphicType && (
      enableCheckPlate ||plateQuantity));
  }

  const updateHiddenFields = (plateType: string, sloganType: string, graphicType: string) => {
    findSectionElementByFieldName(field.parent.ListOfFormSectionElements,'selectionEntered').Value = {plateType, sloganType, graphicType};
  }

  const resetInputError = () => {
    field.Valid = true;
    field.Touched = true;
    field.IsEmpty= false;
    callbackElement(field.parent.parent.parent);
  }

  const handleEmptyInputError = () => {
    field.Valid = false;
    field.Touched = true;
    field.IsEmpty= true;
    callbackElement(field.parent.parent.parent);
  }

  const handleSubmitError = (statusCode) => {
    field.Valid = false;
    field.Touched = true;
    field.IsEmpty = false;
    if (statusCode === SERVICE_CALL_RESPONSE_CODES.PLATE_CHARACTERS_UNAVAILABLE) {
      field.ErrorMessage = 
        'Your plate message is unavailable. The plate message you have entered has already been requested. Please enter a different plate message.';
      field.ErrorMessageFRA = 
        'Votre message de plaque n\'est pas disponible. Le message que vous avez saisi a déjà été demandé. Veuillez saisir un autre message.';
    } else if (statusCode === SERVICE_CALL_RESPONSE_CODES.CONTAINS_INTERCHANGEABLE_CHARACTERS) {
      field.ErrorMessage = 
        'Your plate includes an interchangeable character. The plate message you have entered matches an existing plate due to the use of interchangeable characters (e.g., A/4, O/0). Please enter a different plate message.';
      field.ErrorMessageFRA = 
        'Votre plaque comporte un caractère interchangeable. Le message de plaque que vous avez saisi correspond à une plaque existante en raison de l\'utilisation de caractères interchangeables (p. ex. A/4, O/0). Veuillez saisir un autre message de plaque.';
    } else if (statusCode === SERVICE_CALL_RESPONSE_CODES.PLATE_CHARACTERS_INVALID_FORMAT) {
      field.ErrorMessage = 
        'The plate choice can’t include special characters, start with a space, or have more than 1 space between each character.';
      field.ErrorMessageFRA = 
        'Le choix de la plaque ne peut pas comporter de caractères spéciaux, ni commencer par un espace, ni avoir plus d\'un espace entre chaque caractère.';
    } else if (statusCode === SERVICE_CALL_RESPONSE_CODES.PLATE_INVALID_PREFIX) {
      field.ErrorMessage = 
        'The plate choice can’t start with the characters VA1-VA7, VE0-VE9, V01-V02, VY0-VY.';
      field.ErrorMessageFRA = 
        'Le choix de la plaque ne peut pas commencer par les caractères VA1-VA7, VE0-VE9, V01-V02, VY0-VY.';
    } else if (statusCode === PLATE_ALREADY_EXISTS_IN_ORDER) {
      field.ErrorMessage = 
        'Same plate already exists in the order. Please enter a different plate message.';
      field.ErrorMessageFRA = 
        'Votre plaque existe déjà dans la commande. Veuillez saisir un autre message de plaque.';
    } else {
      field.ErrorMessage =
        'The system is currently unavailable. Due to a technical error, you cannot continue with your personalized licence plate(s) at this time. We apologize for any inconvenience. Please try again later or visit a ServiceOntario centre.';
      field.ErrorMessageFRA = 
        'Le système est actuellement indisponible. En raison d\'une erreur technique, vous ne pouvez pas continuer à personnaliser vos plaques d\'immatriculation pour le moment. Nous vous prions de nous excuser pour ce désagrément. Veuillez réessayer plus tard ou vous rendre dans un centre ServiceOntario.';
    }
    callbackElement(field.parent.parent.parent);
  }

  const handleSubmitException = () => {
		if (field.SubmitButton?.apiErrorRedirectPage) {
			const sequence = Math.max(0, field.SubmitButton.apiErrorRedirectPage - 1);
      callbackStepIndex(sequence);
		}
  }

  const checkPlate = async (plateType: string, sloganChoice: string, graphicChoice: string, recaptchaToken?: string): Promise<boolean> => {
    const programContainer = field.parent.parent.parent as ProgramContainer;
    const token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
    const api = field.Api;
    const apiKey: APIKey = (field.ApiKeyKey && field.ApiKeyValue) ? {key: field.ApiKeyKey, value: field.ApiKeyValue} : null;

    const headers = setBaseHeaders('application/json', token, apiKey);
    let response: any;

    try {
      setIsBusy(true);
      const requestData = constructRequestData(field, plateType, graphicChoice, sloganChoice);
      if (recaptchaToken) {
        requestData.token = recaptchaToken;
      }

      switch (field.SubmissionMethod) {
        case "post":
          response = await axios.post(api, requestData, { headers: headers });
          break;
        case "get":
          const configs = {
            ...{ headers },  // Headers
            ...requestData ? { params: requestData } : undefined,  // Query params
          };
          response = await axios.get(api, configs);
          break;
      }
      
      if (response.status >= 400) {
        handleSubmitException();
        return false;
      }
    } catch (ex) {
      console.log(ex);
      handleSubmitException();
      return false;
    } finally {
      setIsBusy(false);
    }

    if (!response) {
      handleSubmitException();
      return false;
    }
  
    if (response.data.statusCode === SERVICE_CALL_RESPONSE_CODES.PROCESS_SUCCESSFUL) {  // Plate is available
      if (samePlateExistsInSameOrder(field)) {
        handleSubmitError(PLATE_ALREADY_EXISTS_IN_ORDER);
        return false;
      }
      return true;
    } else {
        handleSubmitError(response.data.statusCode);
        return false;
    }
  }

  const constructRequestData = (field: FormSectionElements, plateType: string, graphicChoice: string, sloganChoice: string): CheckPlateRequest => {
    const identificationType = field.findFieldByName('identificationType')?.Value;
    const driverLicenceNumber = field.findFieldByName('driverLicenceNumber')?.Value;
    const registrantInformationNumber = field.findFieldByName('registrantInformationNumber')?.Value;
    const plateNumber = field.Value.toUpperCase();
    return { 
      identificationType,
      driverLicenceNumber,
      registrantInformationNumber,
      plateNumber,
      plateType,
      graphicChoice,
      sloganChoice
    }
  }

  const samePlateExistsInSameOrder = (field: FormSectionElements): boolean => {
    const plateSection = field.findSectionByTitle('Plate personalization');
    const orderItems = plateSection.ListOfFormSubSections.filter((orderItem) => orderItem.ENUTitle === 'personalizedPlateDetails');
    if (orderItems?.length == 1) {
      return false;
    }
    const matchingPlates = orderItems.filter((item) => item.ListOfFormSectionElements.some((element) => element.LoadBindName === 'plateCharacters' && element.Value === field.Value));
    return matchingPlates?.length > 1;
  }

  const getAltText = (): string => {
    const grapicType = graphicChoice || wantGraphic;
    return constructPlateCanvasAltText(field.Value, plateType, sloganChoice, grapicType, isEng);
  }
  
  const [showPlateImage, setShowPlateImage] = useState<boolean>(false); // To control the showing of the plate-image.
  const [isBusy, setIsBusy] = useState<boolean>(false); // To control the showing of the spinner.

  FormValidationUtils.runJavascript(field);

  const buttonField: FormSectionElements = Object.assign({}, field);
  buttonField.ENUTitle = 'Check Plate';
  buttonField.FRATitle = 'Vérifier la plaque';

  const bannerField: FormSectionElements = Object.assign({}, field);
  bannerField.Visible = showPlateImage;
  bannerField.MessageTitle = "This plate message is available.";
  bannerField.MessageTitleFRA = "Ce message est disponible.";
  bannerField.MessageBody = "<p>Personalized Licence Plate requests are subject to review.</p>";
  bannerField.MessageBodyFRA = "<p>Toutes les demandes de plaques d'immatriculation personnalisées sont soumises à un examen.</p>";

  let hintHtml = isEng ? field.Hint : field.HintFR;

  const buttonClass = !field.enableCheckPlate && !field.Valid
    ? "ontario-button ontario-button--secondary ontario-input__error"
    : "ontario-button ontario-button--secondary";

  return (
    <>
      {field.Visible && (
        <div key={'div_' + field.Datatype +'_' + field.Id} id= {'div_' + field.Datatype +'_' + field.Id}>
          {field.enableCheckPlate && <><div key={'fdLbl_div_' + field.Id}>
            {labelControlArray}
          </div>
          <div>
            <LiveValidationErrMsg
              field={field}
              isEng={isEng}
              key={"LiveValidationErrMsg_" + field.LoadBindName}/>
          </div></>}
          <div key={'fdIpt_div_' + field.Id}>
            <span>
              {field.enableCheckPlate && <input className={field.Valid ||!field.Touched ? 'ontario-input ontario-input--20-char-width' : 'ontario-input ontario-input--20-char-width ontario-input__error'}
                id={field.LoadBindName}  key={'fdIpt_' + field.Id} style={{display: 'inline-block', marginRight: '10px'}}
                required={field.Optional !== 'Y'} value={field.Value} readOnly={field.ReadOnly}
                aria-invalid={!field.Valid ||field.Touched ? true : undefined}
                aria-describedby={
                  [!field.Valid && `${field.LoadBindName}-${field.Id}-error`, hintHtml && `hint-text-${field.LoadBindName}`]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                onChange={handleOnChange} onBlur={handleOnBlur} maxLength={8}/>}
              {!field.Recaptcha && !field.SubmitButton?.enableRecaptcha && (
                <button className={buttonClass}
                  style={{display: 'inline-block'}} 
                  id={field.LoadBindName}
                  title={isEng ? field.ENUTitle : field.FRATitle}
                  onClick={(e) => handleButtonClick()}>
                    {field.enableCheckPlate ? (isEng ? buttonField.ENUTitle : buttonField.FRATitle) : (isEng ? field.buttonText : field.buttonTextFra)}
                </button>
              )}
              {(field.Recaptcha || field.SubmitButton?.enableRecaptcha) && (
                <ReCaptchaV3 lang={isEng ? 'en' : 'fr'}>
                  <ReCaptchaButton key={"recaptchbutton"}
                    id={`button_${field.parent.Sequence}_${field.Id}`}
                    title={isEng ? field.ENUTitle : field.FRATitle}
                    action={field.Recaptcha.action || 'submitAction'}
                    buttonHandler={handleButtonClick}>
                      {field.enableCheckPlate ? (isEng ? buttonField.ENUTitle : buttonField.FRATitle) : (isEng ? field.buttonText : field.buttonTextFra)}
                  </ReCaptchaButton>
                </ReCaptchaV3>
              )}
            </span>
          </div>
          <div aria-live="polite" className="ontario__visually-hidden" id="plate-description">
            {getAltText()}
          </div>
          <PlateImageComponent 
            field={field} 
            graphicChoice={graphicChoice || wantGraphic} 
            plateType={plateType} 
            sloganChoice={sloganChoice}
            redraw={redrawPlate}
            setRedraw={setRedrawPlate}
            showImage={showPlateImage}
          />
          {field.enableCheckPlate && <InformationalAlertComponent isEng={isEng} field={bannerField}/>}
          <div>
            {showPlateImage && <div></div>}
            <SpinnerComponent isBusy={isBusy}/>
          </div>
        </div>
      )}
    </>
  );
}

export default CheckPlateAndRenderImageComponent;